<script setup>
import { computed } from 'vue';
import { useDemoLoopStore } from '@/javascript/stores/demoLoop';

const demo = useDemoLoopStore();

const filteredLoops = computed(() => {
  if (!demo.pickerContext) return demo.loopList;
  return demo.loopList.filter(l => demo.loops[l.key].context === demo.pickerContext);
});

function close() {
  demo.pickerOpen = false;
  demo.pickerContext = null;
}
</script>

<template>
  <!--  Menu  -->
  <Transition name="fade">
    <div
      v-if="demo.pickerOpen"
      class="demo-picker-overlay"
      @click.self="close"
    >
      <div class="demo-picker">
        <div class="demo-picker__header">
          <h2>Take a Tour</h2>
          <button
            class="demo-picker__close"
            aria-label="Close"
            @click="close"
          >
            ×
          </button>
        </div>

        <div class="demo-picker__loops">
          <div
            v-for="loop in filteredLoops"
            :key="loop.key"
            class="demo-picker__loop"
          >
            <div class="demo-picker__loop-info">
              <div class="demo-picker__loop-label">
                {{ loop.label }}
              </div>
              <div class="demo-picker__loop-desc">
                {{ loop.description }}
              </div>
            </div>
            <button
              class="demo-picker__start-btn"
              @click="demo.start(loop.key)"
            >
              Start
            </button>
          </div>
        </div>

        <label class="demo-picker__checkbox">
          <input
            v-model="demo.loopContinuously"
            type="checkbox"
          >
          <span>Loop continuously</span>
        </label>

        <div class="demo-picker__hints">
          <span><kbd>Space</kbd> pause</span>
          <span><kbd>Esc</kbd> stop</span>
          <span><kbd>← →</kbd> skip</span>
          <span><kbd>Alt+D</kbd> this menu</span>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss">
.demo-picker-overlay {
  position: fixed;
  inset: 0;
  z-index: 99996;
  display: grid;
  place-items: center;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(3px);
}

.demo-picker {
  background: #fff;
  border-radius: 16px;
  padding: 28px 32px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 { font-size: 1.3rem; font-weight: 700; color: #0f172a; margin: 0; }
  }

  &__close {
    width: 32px; height: 32px; border-radius: 8px;
    border: none; background: #f1f5f9; font-size: 1.3rem;
    cursor: pointer; display: grid; place-items: center;
    color: #64748b;
    &:hover { background: #e2e8f0; }
  }

  &__loops {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
  }

  &__loop {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    transition: border-color 0.15s;

    &:hover { border-color: #10be5a; }
  }

  &__loop-label { font-weight: 600; font-size: 0.95rem; color: #0f172a; }
  &__loop-desc { font-size: 0.8rem; color: #64748b; margin-top: 2px; }

  &__start-btn {
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    background: #10be5a;
    color: #fff;
    font-weight: 700;
    font-size: 0.8rem;
    cursor: pointer;
    white-space: nowrap;
    transition: filter 0.15s;
    &:hover { filter: brightness(0.92); }
  }

  &__checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    color: #475569;
    cursor: pointer;
    margin-bottom: 16px;

    input { accent-color: #10be5a; width: 16px; height: 16px; }
  }

  &__hints {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    font-size: 0.75rem;
    color: #94a3b8;

    kbd {
      padding: 2px 5px;
      border-radius: 4px;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      font-family: inherit;
      font-size: 0.7rem;
    }
  }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
